import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { GLASS_PANEL } from '../../../components/ui/glass';
import { EASE_OUT } from '../../../components/ui/motionPresets';
import useBodyScrollLock from '../../../hooks/useBodyScrollLock';
import { useTranslation } from '../../../i18n/LanguageContext';
import EmployeeDetailPanel from './EmployeeDetailPanel';

// Same surface as the table card it sits next to
const DOCK_SURFACE = [
  'tw-border tw-border-white/80 tw-bg-white/80 tw-shadow-[inset_0_1px_0_rgba(255,255,255,1),0_20px_50px_-30px_rgba(15,23,42,0.35)]',
  'dark:tw-border-white/10 dark:tw-bg-[#22262f]/80 dark:tw-shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_20px_50px_-30px_rgba(0,0,0,0.9)]',
].join(' ');

// Mounted only while the overlay is open: locks page scrolling and moves
// focus to the close button. No focus trap, since the legacy dialogs opened
// from here don't take part in the shared dialog stack.
const DrawerPanel = (props) => {
  const closeButtonRef = useRef(null);
  useBodyScrollLock();
  useEffect(() => {
    closeButtonRef.current?.focus({ preventScroll: true });
  }, []);
  return <EmployeeDetailPanel ref={closeButtonRef} {...props} className={`tw-h-full tw-w-full ${GLASS_PANEL}`} />;
};

/**
 * Selected employee's panel. `docked`: a column beside the table, `height`
 * tall (down to the bottom of the screen; wide screens, rows stay clickable). Otherwise a drawer that
 * slides in from the right over a dimmed page.
 */
const EmployeeDetailPane = ({ docked, height, employee, onClose, ...panelProps }) => {
  const { t, language } = useTranslation();
  if (docked) {
    return (
      <AnimatePresence>
        {employee && (
          <motion.aside
            key="employee-detail"
            aria-label={t('employees.panel.label')}
            initial={{ opacity: 0, x: 24 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 24 }}
            transition={{ duration: 0.25, ease: EASE_OUT }}
            style={{ height: height || undefined }}
            className="tw-flex tw-w-[22rem] tw-shrink-0"
          >
            <EmployeeDetailPanel
              employee={employee}
              onClose={onClose}
              {...panelProps}
              className={`tw-h-full tw-w-full ${DOCK_SURFACE}`}
            />
          </motion.aside>
        )}
      </AnimatePresence>
    );
  }

  return createPortal(
    <AnimatePresence>
      {employee && (
        <motion.div
          key="employee-detail"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          lang={language}
          className="tw-fixed tw-inset-0 tw-z-[999] tw-font-sans tw-antialiased"
        >
          <div aria-hidden="true" onClick={onClose} className="tw-absolute tw-inset-0 tw-bg-slate-900/25 dark:tw-bg-black/50" />
          <motion.aside
            role="dialog"
            aria-label={t('employees.panel.label')}
            initial={{ x: '105%' }}
            animate={{ x: 0 }}
            exit={{ x: '105%' }}
            transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            className="tw-absolute tw-bottom-3 tw-right-3 tw-top-3 tw-flex tw-w-[min(24rem,calc(100vw-1.5rem))]"
          >
            <DrawerPanel employee={employee} onClose={onClose} {...panelProps} />
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};

EmployeeDetailPane.propTypes = {
  docked: PropTypes.bool,
  height: PropTypes.number,
  employee: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default EmployeeDetailPane;
